import React, { useState } from "react";
import {
  OBLIGATION_CATEGORIES,
  OBLIGATION_STATUSES,
  OBLIGATION_PRIORITIES,
  RISK_LEVELS,
  createObligation,
  updateObligation,
} from "../../services/obligationService";

const Field = ({ label, required, children }) => (
  <div className="mb-4">
    <label className="block text-sm font-semibold text-slate-300 mb-1.5">
      {label} {required && <span className="text-yellow-600">*</span>}
    </label>
    {children}
  </div>
);

const Input = ({ ...props }) => (
  <input
    {...props}
    className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded text-slate-100 text-sm font-sans outline-none focus:border-yellow-600 focus:ring-1 focus:ring-yellow-600"
  />
);

const Select = ({ options, ...props }) => (
  <select
    {...props}
    className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded text-slate-100 text-sm font-sans outline-none focus:border-yellow-600 focus:ring-1 focus:ring-yellow-600"
  >
    <option value="">Select...</option>
    {options.map(o => (
      <option key={o} value={o}>
        {o}
      </option>
    ))}
  </select>
);

export const ObligationForm = ({ obligation, contractId, contractRefNo, onSave, onCancel, saving, service }) => {
  const [form, setForm] = useState(
    obligation || {
      title: "",
      description: "",
      category: "",
      responsibleParty: "",
      responsibleEmail: "",
      dueDate: "",
      priority: "High",
      status: "Not Started",
      estimatedDays: "",
      riskLevel: "Medium",
      riskDescription: "",
      stakesDescription: "",
      dependencies: "",
      contingentOn: "",
      notes: "",
      remindersEnabled: true,
      reminderDays: 7,
      escalationEnabled: true,
      escalationDays: 3,
      milestones: [],
    }
  );

  const [milestoneInput, setMilestoneInput] = useState("");

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const addMilestone = () => {
    if (!milestoneInput.trim()) return;
    const newMilestones = [
      ...form.milestones,
      { name: milestoneInput, dueDate: "", completed: false },
    ];
    setForm(p => ({ ...p, milestones: newMilestones }));
    setMilestoneInput("");
  };

  const removeMilestone = (idx) => {
    const updated = form.milestones.filter((_, i) => i !== idx);
    setForm(p => ({ ...p, milestones: updated }));
  };

  const handleSubmit = async () => {
    const required = ["title", "category", "responsibleParty", "dueDate", "priority"];
    const missing = required.filter(k => !form[k]);
    if (missing.length) {
      alert("Please fill all required fields");
      return;
    }

    try {
      if (obligation?.id) {
        await updateObligation(service, obligation.id, form);
      } else {
        await createObligation(service, contractId, {
          ...form,
          contractRefNo,
          createdBy: "System",
        });
      }
      onSave();
    } catch (err) {
      alert("Error saving obligation: " + err.message);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-5">
      <div className="bg-slate-900 border border-slate-700 rounded-lg max-w-2xl w-full max-h-screen overflow-auto">
        <div className="p-6 border-b border-slate-700 sticky top-0 bg-slate-900">
          <h2 className="m-0 text-slate-100 text-xl font-semibold">
            {obligation ? "Edit Obligation" : "New Obligation"}
          </h2>
          <p className="mt-1 text-slate-400 text-sm">Contract: {contractRefNo}</p>
        </div>

        <div className="p-6">
          {/* Basic Info Section */}
          <div className="mb-7 pb-7 border-b border-slate-700">
            <h3 className="text-yellow-600 text-sm font-semibold m-0 mb-4 uppercase tracking-wider">
              Basic Information
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <Field label="Obligation Title" required>
                <Input value={form.title} onChange={e => set("title", e.target.value)} placeholder="e.g., Submit Q1 Report" />
              </Field>
              <Field label="Category" required>
                <Select options={OBLIGATION_CATEGORIES} value={form.category} onChange={e => set("category", e.target.value)} />
              </Field>
            </div>
            <Field label="Description">
              <textarea
                value={form.description}
                onChange={e => set("description", e.target.value)}
                placeholder="Detailed description of the obligation..."
                rows={3}
                className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded text-slate-100 text-sm font-sans outline-none focus:border-yellow-600 resize-vertical"
              />
            </Field>
          </div>

          {/* Responsibility Section */}
          <div className="mb-7 pb-7 border-b border-slate-700">
            <h3 className="text-yellow-600 text-sm font-semibold m-0 mb-4 uppercase tracking-wider">
              Responsibility & Timeline
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <Field label="Responsible Party" required>
                <Input value={form.responsibleParty} onChange={e => set("responsibleParty", e.target.value)} placeholder="e.g., Finance Team" />
              </Field>
              <Field label="Contact Email" required>
                <Input type="email" value={form.responsibleEmail} onChange={e => set("responsibleEmail", e.target.value)} placeholder="email@example.com" />
              </Field>
              <Field label="Due Date" required>
                <Input type="date" value={form.dueDate} onChange={e => set("dueDate", e.target.value)} />
              </Field>
              <Field label="Estimated Days to Complete">
                <Input type="number" min="0" value={form.estimatedDays} onChange={e => set("estimatedDays", e.target.value)} placeholder="Days" />
              </Field>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Priority" required>
                <Select options={OBLIGATION_PRIORITIES} value={form.priority} onChange={e => set("priority", e.target.value)} />
              </Field>
              <Field label="Status" required>
                <Select options={OBLIGATION_STATUSES} value={form.status} onChange={e => set("status", e.target.value)} />
              </Field>
            </div>
          </div>

          {/* Risk Assessment Section */}
          <div className="mb-7 pb-7 border-b border-slate-700">
            <h3 className="text-yellow-600 text-sm font-semibold m-0 mb-4 uppercase tracking-wider">
              Risk Assessment
            </h3>
            <Field label="Risk Level" required>
              <Select options={RISK_LEVELS} value={form.riskLevel} onChange={e => set("riskLevel", e.target.value)} />
            </Field>
            <Field label="What Could Go Wrong?">
              <textarea
                value={form.riskDescription}
                onChange={e => set("riskDescription", e.target.value)}
                placeholder="Describe potential risks and mitigation strategies..."
                rows={3}
                className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded text-slate-100 text-sm font-sans outline-none focus:border-yellow-600 resize-vertical"
              />
            </Field>
            <Field label="What's at Stake?">
              <textarea
                value={form.stakesDescription}
                onChange={e => set("stakesDescription", e.target.value)}
                placeholder="Business impact if this obligation is not met..."
                rows={3}
                className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded text-slate-100 text-sm font-sans outline-none focus:border-yellow-600 resize-vertical"
              />
            </Field>
          </div>

          {/* Milestones Section */}
          <div className="mb-7 pb-7 border-b border-slate-700">
            <h3 className="text-yellow-600 text-sm font-semibold m-0 mb-4 uppercase tracking-wider">
              Milestones
            </h3>
            <div className="flex gap-2 mb-3">
              <Input value={milestoneInput} onChange={e => setMilestoneInput(e.target.value)} onKeyPress={e => e.key === "Enter" && addMilestone()} placeholder="Add a milestone..." />
              <button
                onClick={addMilestone}
                className="px-4 py-2.5 bg-yellow-600 border-none rounded text-slate-900 cursor-pointer font-semibold whitespace-nowrap hover:bg-yellow-500"
              >
                Add
              </button>
            </div>
            {form.milestones.length > 0 && (
              <div className="bg-slate-800 rounded p-3">
                {form.milestones.map((m, i) => (
                  <div key={i} className={`flex items-center gap-2.5 py-2 ${i < form.milestones.length - 1 ? "border-b border-slate-700" : ""}`}>
                    <input
                      type="checkbox"
                      checked={m.completed}
                      onChange={e => {
                        const updated = [...form.milestones];
                        updated[i].completed = e.target.checked;
                        set("milestones", updated);
                      }}
                      className="accent-yellow-600"
                    />
                    <span className={`flex-1 text-slate-100 text-sm ${m.completed ? "line-through text-slate-500" : ""}`}>{m.name}</span>
                    <button
                      onClick={() => removeMilestone(i)}
                      className="bg-none border-none text-slate-400 hover:text-slate-200 cursor-pointer text-base"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Dependencies Section */}
          <div className="mb-6">
            <h3 className="text-yellow-600 text-sm font-semibold m-0 mb-4 uppercase tracking-wider">
              Dependencies & Notifications
            </h3>
            <Field label="Dependencies (comma-separated)">
              <Input value={form.dependencies} onChange={e => set("dependencies", e.target.value)} placeholder="Other obligations this depends on..." />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="flex items-center gap-2.5 p-3 bg-slate-800 rounded cursor-pointer text-slate-100">
                <input
                  type="checkbox"
                  checked={form.remindersEnabled}
                  onChange={e => set("remindersEnabled", e.target.checked)}
                  className="accent-yellow-600"
                />
                <span className="text-sm">Send Reminders</span>
              </label>
              {form.remindersEnabled && (
                <Field label="Days Before Due">
                  <Input type="number" min="1" value={form.reminderDays} onChange={e => set("reminderDays", e.target.value)} />
                </Field>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
              <label className="flex items-center gap-2.5 p-3 bg-slate-800 rounded cursor-pointer text-slate-100">
                <input
                  type="checkbox"
                  checked={form.escalationEnabled}
                  onChange={e => set("escalationEnabled", e.target.checked)}
                  className="accent-yellow-600"
                />
                <span className="text-sm">Enable Escalation</span>
              </label>
              {form.escalationEnabled && (
                <Field label="Days Overdue to Escalate">
                  <Input type="number" min="1" value={form.escalationDays} onChange={e => set("escalationDays", e.target.value)} />
                </Field>
              )}
            </div>
          </div>

          {/* Notes Section */}
          <div className="mb-6">
            <Field label="Internal Notes">
              <textarea
                value={form.notes}
                onChange={e => set("notes", e.target.value)}
                placeholder="Internal notes, decision rationale, etc..."
                rows={3}
                className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded text-slate-100 text-sm font-sans outline-none focus:border-yellow-600 resize-vertical"
              />
            </Field>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 justify-end pt-4 border-t border-slate-700">
            <button
              onClick={onCancel}
              disabled={saving}
              className={`px-5 py-2.5 bg-slate-800 border border-slate-700 rounded text-slate-400 cursor-pointer font-semibold ${
                saving ? "opacity-50 cursor-not-allowed" : "hover:bg-slate-700"
              }`}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={saving}
              className={`px-6 py-2.5 bg-green-700 border-none rounded text-white cursor-pointer font-semibold ${
                saving ? "opacity-50 cursor-not-allowed" : "hover:bg-green-600"
              }`}
            >
              {saving ? "Saving..." : "Save Obligation"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
